import { useEffect, useMemo, useState } from 'react';
import { getInquiries, setInquiryStatus, deleteInquiry, getTeams, Inquiry, Team } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import '../components/TeamNav.css';
import './Enquiries.css';

/**
 * Enquiries: every message from the website's Contact and Join pages, and who
 * it went to. Club and Pro. A new one goes on a team's waiting list or is marked
 * handled; new ones badge the sidebar.
 */

type Status = 'new' | 'waiting' | 'handled';
type Kind = 'all' | 'join' | 'contact';

const NO_TEAM = 'A team that no longer exists';

const destKey = (e: Inquiry) => (e.routedTo.kind === 'club' ? 'club' : `${e.routedTo.kind}:${e.routedTo.id || ''}`);

function destLabel(e: Inquiry): string {
  if (e.routedTo.kind === 'club') return 'The club in general';
  return e.routedTo.label || (e.routedTo.kind === 'team' ? 'A team' : 'A committee member');
}

/** Older enquiries only know the destination, not the addresses. */
function routeLine(e: Inquiry): string {
  const direct = e.routedTo.direct || [];
  const sent = e.sentTo || [];
  const copied = sent.filter(s => !direct.includes(s));
  if (e.routedTo.kind !== 'club') {
    const to = direct.length ? ` (${direct.join(', ')})` : '';
    const cc = copied.length ? `, copied to ${copied.join(', ')}` : '';
    return `${destLabel(e)}${to}${cc}`;
  }
  const to = sent.length ? ` (${sent.join(', ')})` : '';
  const meant = e.routedTo.fallback
    ? `. It was meant for ${e.forLabel || 'a team or committee member'} but went to the club inbox.`
    : '';
  return `The club in general${to}${meant}`;
}

function when(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function day(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function waited(iso?: string): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return '';
  const days = Math.max(0, Math.floor((Date.now() - t) / 86400000));
  return days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`;
}

const byName = (a: string, b: string) => a.localeCompare(b, 'en', { numeric: true });

export default function Enquiries() {
  const { clubName } = useAuth();
  const [rows, setRows] = useState<Inquiry[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Status>('new');
  const [kind, setKind] = useState<Kind>('all');
  const [dest, setDest] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [list, clubTeams] = await Promise.all([getInquiries(), getTeams().catch(() => [] as Team[])]);
      setRows(list);
      setTeams([...clubTeams].sort((a, b) => byName(a.name, b.name)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changed = () => window.dispatchEvent(new Event('enquiries-changed'));
  const teamName = (id?: string) => teams.find(t => t.id === id)?.name || '';
  const waitingLabel = (e: Inquiry) => e.waitingTeamName || teamName(e.waitingTeamId) || NO_TEAM;

  /** Where a card's team picker starts: its current list, or the team it was sent to. */
  const pickFor = (e: Inquiry) => {
    if (picked[e.inquiryId] !== undefined) return picked[e.inquiryId];
    if (e.status === 'waiting') return e.waitingTeamId || '';
    return e.routedTo.kind === 'team' && teams.some(t => t.id === e.routedTo.id) ? e.routedTo.id || '' : '';
  };

  const update = async (e: Inquiry, status: Status, teamId?: string) => {
    setBusy(e.inquiryId);
    setError('');
    try {
      await setInquiryStatus(e.inquiryId, status, teamId);
      const now = new Date().toISOString();
      setRows(list => list.map(r => {
        if (r.inquiryId !== e.inquiryId) return r;
        if (status === 'waiting') {
          return {
            ...r, status, waitingTeamId: teamId, waitingTeamName: teamName(teamId),
            waitingSince: r.waitingSince || now, handledAt: undefined, handledBy: undefined,
          };
        }
        if (status === 'handled') return { ...r, status, handledAt: now };
        return {
          ...r, status, handledAt: undefined, handledBy: undefined,
          waitingTeamId: undefined, waitingTeamName: undefined, waitingSince: undefined, waitingBy: undefined,
        };
      }));
      setPicked(p => {
        const next = { ...p };
        delete next[e.inquiryId];
        return next;
      });
      changed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the enquiry');
    } finally {
      setBusy('');
    }
  };

  const remove = async (e: Inquiry) => {
    if (!window.confirm(`Delete the enquiry from ${e.name} for good? This can't be undone.`)) return;
    setBusy(e.inquiryId);
    setError('');
    try {
      await deleteInquiry(e.inquiryId);
      setRows(list => list.filter(r => r.inquiryId !== e.inquiryId));
      changed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the enquiry');
    } finally {
      setBusy('');
    }
  };

  const destinations = useMemo(() => {
    const seen = new Map<string, { label: string; count: number }>();
    for (const e of rows) {
      const k = destKey(e);
      seen.set(k, { label: destLabel(e), count: (seen.get(k)?.count || 0) + 1 });
    }
    return [...seen.entries()].sort(([a, x], [b, y]) =>
      (a === 'club' ? -1 : b === 'club' ? 1 : byName(x.label, y.label)));
  }, [rows]);

  const waitingTeams = useMemo(() => {
    const seen = new Map<string, { label: string; count: number }>();
    for (const e of rows.filter(r => r.status === 'waiting')) {
      const k = e.waitingTeamId || '';
      seen.set(k, { label: waitingLabel(e), count: (seen.get(k)?.count || 0) + 1 });
    }
    return [...seen.entries()].sort(([, x], [, y]) => byName(x.label, y.label));
  }, [rows, teams]);

  const count = (s: Status) => rows.filter(r => r.status === s).length;
  const inTab = rows.filter(r => r.status === tab && (kind === 'all' || r.type === kind));
  const visible = tab === 'waiting'
    ? inTab.filter(r => teamFilter === 'all' || (r.waitingTeamId || '') === teamFilter)
    : inTab.filter(r => dest === 'all' || destKey(r) === dest);

  // The waiting list reads by team, longest wait first.
  const groups = tab === 'waiting'
    ? waitingTeams
      .map(([k, g]) => ({
        key: k,
        label: g.label,
        items: visible
          .filter(r => (r.waitingTeamId || '') === k)
          .sort((a, b) => String(a.waitingSince || a.createdAt).localeCompare(String(b.waitingSince || b.createdAt))),
      }))
      .filter(g => g.items.length > 0)
    : [{ key: 'all', label: '', items: visible }];

  const reply = (e: Inquiry) => {
    const subject = `Your ${e.type === 'join' ? 'join request' : 'message'} to ${clubName || 'the club'}`;
    return `mailto:${e.email}?subject=${encodeURIComponent(subject)}`;
  };

  const teamPicker = (e: Inquiry, label: string) => (
    <div>
      <label htmlFor={`wl-${e.inquiryId}`}>{label}</label>
      <select
        id={`wl-${e.inquiryId}`}
        value={pickFor(e)}
        onChange={ev => setPicked(p => ({ ...p, [e.inquiryId]: ev.target.value }))}
      >
        <option value="">Choose a team…</option>
        {teams.map(t => (
          <option key={t.id} value={t.id}>{t.name}{t.ageGroup ? ` (${t.ageGroup})` : ''}</option>
        ))}
      </select>
    </div>
  );

  const card = (e: Inquiry) => {
    const choice = pickFor(e);
    const isBusy = busy === e.inquiryId;
    return (
      <div key={e.inquiryId} className="card-surface enq-card">
        <div className="enq-main">
          <div className="enq-head">
            <h3 className="enq-name">{e.name}</h3>
            <span className={`enq-chip enq-chip-${e.type}`}>
              {e.type === 'join' ? 'Join request' : 'Contact message'}
            </span>
          </div>
          <p className="enq-when">{when(e.createdAt)}</p>

          {e.status === 'waiting' && (
            <p className="enq-detail">
              <span className="enq-label">Waiting list</span>
              {waitingLabel(e)}, since {day(e.waitingSince)} ({waited(e.waitingSince)})
            </p>
          )}
          <p className="enq-detail">
            <span className="enq-label">Went to</span>
            {routeLine(e)}
          </p>
          <p className="enq-detail">
            <span className="enq-label">From</span>
            <a href={`mailto:${e.email}`}>{e.email}</a>
            {e.phone && <> · <a href={`tel:${e.phone.replace(/\s+/g, '')}`}>{e.phone}</a></>}
          </p>

          {e.details.some(d => d.value) && (
            <dl className="enq-answers">
              {e.details.filter(d => d.value).map((d, i) => (
                <div key={i}>
                  <dt>{d.label}</dt>
                  <dd>{d.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {e.text && <p className="enq-message">{e.text}</p>}
        </div>

        <div className="enq-actions">
          <a className="ebtn ebtn-primary" href={reply(e)}>Reply by email</a>

          {e.status === 'new' && (
            <>
              {teams.length > 0 && teamPicker(e, 'Waiting list for')}
              {teams.length > 0 && (
                <button
                  className="ebtn ebtn-secondary"
                  disabled={isBusy || !choice}
                  onClick={() => update(e, 'waiting', choice)}
                  title={!choice ? 'Choose a team first' : undefined}
                >
                  Add to waiting list
                </button>
              )}
              <button className="ebtn ebtn-secondary" disabled={isBusy} onClick={() => update(e, 'handled')}>
                Mark as handled
              </button>
            </>
          )}

          {e.status === 'waiting' && (
            <>
              <button className="ebtn ebtn-secondary" disabled={isBusy} onClick={() => update(e, 'handled')}>
                Mark as handled
              </button>
              {teamPicker(e, 'Move to another team')}
              <button
                className="ebtn ebtn-secondary"
                disabled={isBusy || !choice || choice === e.waitingTeamId}
                onClick={() => update(e, 'waiting', choice)}
              >
                Move
              </button>
              <button className="ebtn ebtn-secondary" disabled={isBusy} onClick={() => update(e, 'new')}>
                Move back to New
              </button>
            </>
          )}

          {e.status === 'handled' && (
            <button className="ebtn ebtn-secondary" disabled={isBusy} onClick={() => update(e, 'new')}>
              Move back to New
            </button>
          )}

          <button className="ebtn ebtn-danger" disabled={isBusy} onClick={() => remove(e)}>
            Delete
          </button>

          {e.status === 'handled' && e.handledAt && (
            <p className="enq-hint">
              Handled {when(e.handledAt)}{e.handledBy ? ` by ${e.handledBy}` : ''}
              {e.waitingTeamId ? `. Was on the ${waitingLabel(e)} waiting list.` : ''}
            </p>
          )}
        </div>
      </div>
    );
  };

  const empty = {
    new: 'No new enquiries.',
    waiting: 'Nobody is on a waiting list.',
    handled: 'Nothing marked as handled yet.',
  }[tab];

  return (
    <div className="tm-page-shell">
      <div className="page-head">
        <div>
          <h1>Enquiries</h1>
          <p className="subtitle">
            Messages from your website's Contact and Join pages, and who each one went to.
          </p>
        </div>
        <div className="head-actions">
          <button className="ebtn ebtn-secondary" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <nav className="tm-nav" aria-label="Enquiry sections">
        <div className="tm-nav-tabs">
          <button className={`tm-nav-tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>
            New{count('new') ? ` (${count('new')})` : ''}
          </button>
          <button className={`tm-nav-tab ${tab === 'waiting' ? 'active' : ''}`} onClick={() => setTab('waiting')}>
            Waiting list{count('waiting') ? ` (${count('waiting')})` : ''}
          </button>
          <button className={`tm-nav-tab ${tab === 'handled' ? 'active' : ''}`} onClick={() => setTab('handled')}>
            Handled{count('handled') ? ` (${count('handled')})` : ''}
          </button>
        </div>
      </nav>

      {rows.length > 0 && (
        <div className="enq-filters">
          <label>
            Type
            <select value={kind} onChange={ev => setKind(ev.target.value as Kind)}>
              <option value="all">All messages</option>
              <option value="join">Join requests</option>
              <option value="contact">Contact messages</option>
            </select>
          </label>
          {tab === 'waiting' ? (
            <label>
              Team
              <select value={teamFilter} onChange={ev => setTeamFilter(ev.target.value)}>
                <option value="all">All teams</option>
                {waitingTeams.map(([k, g]) => (
                  <option key={k} value={k}>{g.label} ({g.count})</option>
                ))}
              </select>
            </label>
          ) : (
            <label>
              Went to
              <select value={dest} onChange={ev => setDest(ev.target.value)}>
                <option value="all">Anyone</option>
                {destinations.map(([k, d]) => (
                  <option key={k} value={k}>{d.label} ({d.count})</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {error && <div className="ealert ealert-error">{error}</div>}

      {loading && rows.length === 0 && <div className="empty-state"><p>Loading…</p></div>}

      {!loading && visible.length === 0 && (
        <div className="empty-state">
          <p><strong>{empty}</strong></p>
          <p className="tm-muted">
            {rows.length === 0
              ? "When someone uses your website's Contact or Join page, their message appears here."
              : tab === 'waiting' && count('waiting') === 0
                ? 'From the New tab, choose a team and add an enquiry to its waiting list.'
                : 'Nothing here matches these filters.'}
          </p>
        </div>
      )}

      {groups.map(g => (
        <section key={g.key} className="enq-group">
          {g.label && (
            <h2 className="enq-group-title">
              {g.label}
              <span className="enq-group-count">{g.items.length} waiting</span>
            </h2>
          )}
          {g.items.map(card)}
        </section>
      ))}
    </div>
  );
}
